import React from 'react';
import { HeartHandshake, MapPin, ListChecks, MessageCircle } from 'lucide-react';
import Seo from '@/components/Seo';
import { EmergencyCta, SectionHeading } from '@/components/sections';
import { IMAGES } from '@/lib/site';

const VALUES = [
  { icon: MapPin, title: 'Tri-Cities Focus', text: 'The service area is intentionally limited to Coquitlam, Port Coquitlam and Port Moody.' },
  { icon: ListChecks, title: 'Simple First Step', text: 'Tell us where you are and what happened without filling out a long intake form.' },
  { icon: MessageCircle, title: 'Clear Information', text: 'The site explains common water damage situations in plain language so homeowners can understand the next step.' },
  { icon: HeartHandshake, title: 'Connection Service', text: 'Tri-Cities Water Restoration helps homeowners request assistance from independent restoration professionals serving the area.' },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Tri-Cities Water Restoration"
        description="Learn how Tri-Cities Water Restoration helps homeowners in Coquitlam, Port Coquitlam and Port Moody request local water damage assistance."
      />
      <section className="relative overflow-hidden bg-navy">
        <img src={IMAGES.tech} alt="Water damage restoration equipment" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
        <div className="container-page relative py-16 lg:py-20">
          <div className="max-w-3xl">
            <span className="inline-block rounded-full bg-water/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-water/20">About</span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">A simpler way to request local <span className="text-water">water damage help</span>.</h1>
            <p className="mt-5 text-lg leading-relaxed text-white/70">Tri-Cities Water Restoration is a local connection service for homeowners and property owners in Coquitlam, Port Coquitlam and Port Moody. The goal is simple: make it easier to describe the problem and request the right kind of restoration help.</p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-page">
          <SectionHeading center eyebrow="How We Help" title="Focused on a clear homeowner experience" sub="The site is designed around three things: the problem, the location, and the fastest reasonable next step." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-2xl border border-border bg-white p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-water-light text-water-strong"><v.icon className="h-5 w-5" strokeWidth={2} /></span>
                <h2 className="mt-4 font-display text-lg font-bold text-primary">{v.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary py-16">
        <div className="container-page max-w-3xl">
          <SectionHeading eyebrow="What We Are" title="A connection platform, not the restoration contractor" sub="Independent restoration professionals provide the on-site assessment and restoration work. This website is the local request and information layer that helps homeowners start that process." />
        </div>
      </section>

      <EmergencyCta />
    </>
  );
}
